import Vue from "vue";
import OrderApi from "../../../../services/api/orderApi.js";
import DeclineOrderModal from "./declineOrderModal/declineOrderModal.vue";
import ParseProductUrlModal from "./parseProductUrlModal/parseProductUrlModal.vue";
import BrandApi from "../../../../services/api/brandApi.js";
import { mapGetters } from "vuex";
import { algoliaBaseHelper } from "@/components/mixin/search/algoliaBaseHelper";
import SubOrder from "@/views/dashboard/orders/orderDetails/sub-order";

const OrderApiService = new OrderApi();
const BrandService = new BrandApi();

export default {
  mixins: [algoliaBaseHelper],
  components: {
    SubOrder,
    DeclineOrderModal,
    ParseProductUrlModal,
  },

  data() {
    return {
      isShopifyConnected: false,
      shopifyUrl: null,
      hasShopifyOrder: false,
      declineOrderId: null,
      isParseProductUrlModalVisible: false,
      addToSuborder: null,
      carriers: [],
      order: null,
      chatMe: null,
      showChatButton: false,
      updatePackingSlipButtonDisabled: false,
      updateInProgress: false,
    };
  },

  computed: {
    isAdmin,
    ...mapGetters({
      user: "userState",
    }),
    showShopify() {
      const status = this.order.suborders[0].status;
      return (
        this.user.role === "BRAND" &&
        this.hasShopifyOrder &&
        (status === "APPROVED" || status === "SHIPPED" || status === "FULFILLMENT_IN_PROGRESS")
      );
    },
    showPartialFulfillmentWarning() {
      return (
        this.user.role === "BRAND" && this.hasShopifyOrder && this.order.suborders[0].status === "PARTIALLY_FULFILLED"
      );
    },
  },

  methods: {
    openDeclineOrderModal,
    closeDeclineOrderModal,
    selectShippingCarrierCallback,
    getShippingCarriers,
    goToPackingSlip,
    back,
    getData,
    getOrder,
    getSuborder,
    decline,
    validateSuborder,
    update,
    checkShippingInfo,
    triggerPartiallyUpdate,
    updateShippingInfo,
    completeOrder,
    updatePackingSlip,
    productFound,
    adjustmentModalCallback,
    addProductToOrder,
    adjustAcceptSuborder,
    adjustDeclineSuborder,
    adjustSuborder,
    canSubsequentlyApproveRequest,
    canSubsequentlyDeclineRequest,
    fulfilledQuantityIsEditable,
    setBrandInfoForChat,
    setUpShopify,
  },

  async beforeMount() {
    if (this.user.role === "ADMIN") {
      this.getOrder();
    } else {
      this.getSuborder();
    }
    this.getShippingCarriers();

    if (this.user.role === "BRAND") {
      const { data } = await BrandService.getBrandMe();
      this.isShopifyConnected = data.shopify !== null;
      await this.setBrandInfoForChat(data);
      await this.setUpShopify();
    }
  },

  mounted() {
    this.$eventBus.$on("order-adjustment-product-added", this.adjustmentModalCallback);
  },

  beforeDestroy() {
    this.$eventBus.$off("order-adjustment-product-added", this.adjustmentModalCallback);
  },
};

async function setUpShopify(wait = false) {
  const attempts = wait ? 5 : 0,
    timeoutDuration = 3000;
  this.subOrderIndex = this.algoliaClient.initIndex(this.$env.getOrigin() + "_suborders");

  const search = (count) => {
    this.subOrderIndex.clearCache();
    this.subOrderIndex.search(this.$route.params.suborderId, { hitsPerPage: 1 }).then(({ hits }) => {
      const shopifyData = hits[0].shopify;

      if (shopifyData) {
        this.hasShopifyOrder = true;
        this.shopifyUrl = shopifyData.order_url;
      } else {
        const dec = count - 1;
        if (dec === -1) {
          return;
        }
        setTimeout(() => {
          search(dec);
        }, timeoutDuration);
      }
    });
  };
  search(attempts);
}

async function setBrandInfoForChat(me) {
  this.chatMe = {
    fullName: `${me.first_name} from ${me.brand_name}`,
    role: "BRAND",
    userId: this.user.brand_id,
    email: me.email,
  };
  this.showChatButton = true;
}

function openDeclineOrderModal(suborderId) {
  this.declineOrderId = suborderId;
}

function closeDeclineOrderModal(data) {
  const suborder = this.order.suborders.find((request) => request.id === this.declineOrderId);

  if (data) {
    if (suborder.status === "PENDING") {
      this.decline(this.declineOrderId, data);
    } else {
      this.adjustDeclineSuborder(this.declineOrderId, data);
    }
  } else {
    this.declineOrderId = null;
  }
}

function selectShippingCarrierCallback(val, packing_slip_index, suborder_index) {
  this.order.suborders[suborder_index].packing_slips[packing_slip_index].shipping_carrier = val.name;
}

function goToPackingSlip(suborder, isCreateNew, packingSlipId) {
  let data = {
    order: { ...suborder, address: this.order.address },
  };

  this.$store.commit("setDataForPackingSlip", data);

  let id = this.$route.params.suborderId ? this.$route.params.suborderId : this.$route.params.orderId;

  if (isCreateNew) {
    this.$router.push("/dashboard/orders/" + id + "/create-packing-slip");
  } else {
    this.$router.push("/dashboard/orders/" + id + "/packingSlips/" + packingSlipId + "/review-packing-slip");
  }
}

function back() {
  this.$router.push({
    name: "orders",
    query: this.$route.query,
  });
}

function validateSuborder(suborder, scope) {
  let _self = this;

  _self.$validator.validateAll(scope).then((valid) => {
    if (!valid) {
      return false;
    } else {
      _self.update(suborder);
    }
  });
}

function adjustAcceptSuborder(id) {
  this.adjustSuborder(id, {
    suborder_status: "APPROVED",
  });
}

function adjustDeclineSuborder(id, { decline_order_reason, additional_information }) {
  const declineSuborder = {
    suborder_status: "DECLINED",
    decline_order_reason,
  };

  if (decline_order_reason === "OTHER") {
    declineSuborder["additional_information"] = additional_information;
  }

  this.adjustSuborder(id, declineSuborder);
  this.declineOrderId = null;
}

function adjustSuborder(id, adjustment) {
  this.$loader.$emit("trigger", true);

  OrderApiService.adjustSuborder(id, adjustment)
    .then((response) => {
      this.$swal("Success", "Suborder successfully adjusted!").then(() => {
        this.getData();
      });
    })
    .catch((error) => {
      if (error.response) {
        this.$swal("Attention", error.response.data.error_message);
      } else {
        this.$logger.notify(new Error(error));
      }
    })
    .finally(() => this.$loader.$emit("trigger", false));
}

function update(suborder) {
  if (this.updateInProgress) {
    return;
  }

  this.updateInProgress = true;

  let _self = this;
  _self.$loader.$emit("trigger", true);

  let request = {
    id: suborder.id,
    data: {
      items: [],
    },
  };

  suborder.items.forEach(function (item) {
    request.data.items.push({
      suborder_item_id: item.id,
      fulfilled_quantity: Number(item.fulfilled_quantity),
    });
  });

  if (suborder.status === "PENDING") {
    OrderApiService.updateAcceptSuborder(request)
      .then((response) => {
        _self.$loader.$emit("trigger", false);
        _self
          .$swal(
            "Success",
            `Suborder successfully accepted!  ${
              this.isShopifyConnected
                ? "Please go to your Shopify account to manage and fulfill your order."
                : "You can add shipping info for this suborder."
            }`
          )
          .then(function () {
            _self.getData();
          });
      })
      .catch((error) => {
        _self.$loader.$emit("trigger", false);
        if (error.response) {
          _self.$swal("Attention", error.response.data.error_message);
        } else {
          _self.$logger.notify(new Error(error));
        }
      })
      .finally(() => {
        this.updateInProgress = false;
      });
  } else {
    OrderApiService.updateSuborder(request)
      .then((response) => {
        _self.$loader.$emit("trigger", false);
        _self.$swal("Success", "Suborder successfully updated!").then(function () {
          _self.getData();
        });
      })
      .catch((error) => {
        _self.$loader.$emit("trigger", false);
        if (error.response) {
          _self.$swal("Attention", error.response.data.error_message);
        } else {
          _self.$logger.notify(new Error(error));
        }
      })
      .finally(() => {
        this.updateInProgress = false;
      });
  }
}

function checkShippingInfo(suborder) {
  if (suborder.status === "APPROVED") {
    this.triggerPartiallyUpdate(suborder);
  } else {
    this.updateShippingInfo(suborder);
  }
}

function completeOrder(suborder) {
  const _self = this;

  _self.$loader.$emit("trigger", true);

  OrderApiService.completeOrder({ id: suborder.id })
    .then((response) => {
      _self.$loader.$emit("trigger", false);
      _self.$swal("Success", "Suborder successfully completed!").then(function () {
        _self.getData();
      });
    })
    .catch((error) => {
      _self.$loader.$emit("trigger", false);
      if (error.response) {
        _self.$swal("Attention", error.response.data.error_message);
      } else {
        _self.$logger.notify(new Error(error));
      }
    });
}

function updateShippingInfo(suborder) {
  let _self = this,
    request = {
      id: suborder.id,
      data: {
        shipping_info: suborder.shipping_info,
      },
    };

  _self.$loader.$emit("trigger", true);

  OrderApiService.updateShippingInfo(request)
    .then((response) => {
      _self.$loader.$emit("trigger", false);
      _self.$swal("Success", "You have successfully updated shipping info for this suborder.").then(function () {
        this.$ma.trackEvent({
          action: "Shipping Quantity Updated",
          properties: {
            "Order Id": suborder.number,
            "Sub Order Id": suborder.order_number,
            "Brand Name": suborder.brand_name,
            "Product Id(s)": suborder.items.map((item) => item.id),
            "Product Name(s)": suborder.items.map((item) => item.product_name),
          },
        });
        _self.getData();
      });
    })
    .catch((error) => {
      _self.$loader.$emit("trigger", false);
      if (error.response) {
        _self.$swal("Attention", error.response.data.error_message);
      } else {
        _self.$logger.notify(new Error(error));
      }
    });
}

function triggerPartiallyUpdate(suborder) {
  const _self = this;

  OrderApiService.triggerPartiallyUpdate(suborder.id)
    .then((response) => {
      _self.updateShippingInfo(suborder);
    })
    .catch((error) => {
      if (error.response) {
        _self.$swal("Attention", error.response.data.error_message);
      } else {
        _self.$logger.notify(new Error(error));
      }
    });
}

function decline(id, data) {
  let _self = this;

  _self.$loader.$emit("trigger", true);

  OrderApiService.declineSuborder(id, data)
    .then((response) => {
      _self.$swal("Success", "Suborder successfully declined!").then(function () {
        _self.getData();
      });
      this.$ma.trackEvent({
        action: "Decline Suborder",
        properties: {
          "Order Id": id,
          additional_information: data.additional_information ? data.additional_information : "",
          decline_order_reason: data.decline_order_reason ? data.decline_order_reason : "",
        },
      });
    })
    .catch((error) => {
      if (error.response) {
        _self.$swal("Attention", error.response.data.error_message);
      } else {
        _self.$logger.notify(new Error(error));
      }
    })
    .finally(() => {
      _self.declineOrderId = null;
      _self.$loader.$emit("trigger", false);
    });
}

function getOrder() {
  const _self = this;

  _self.$loader.$emit("trigger", true);

  OrderApiService.getOrder(_self.$route.params.orderId)
    .then((response) => {
      _self.order = response.data;

      _self.order.suborders.forEach(async function (suborder, index) {
        if (_self.isAdmin){
          const {data} = await BrandService.getBrand(suborder.brand.id);
          Vue.set(suborder, "isShopify", data.shopify !== null);
        }
        suborder.items.forEach(function (item) {
          Vue.set(item, "original_fulfilled_quantity", item.fulfilled_quantity);
        });

        _self.$loader.$emit("trigger", false);
      });
    })
    .catch((error) => {
      _self.$loader.$emit("trigger", false);
      if (error.response) {
        _self.$swal("Attention", error.response.data.error_message);
      } else {
        _self.$logger.notify(new Error(error));
      }
    });
}

function getSuborder() {
  let _self = this;

  OrderApiService.getSuborder(this.$route.params.suborderId)
    .then((response) => {
      _self.order = {
        suborders: [response.data],
        address: response.data?.address,
      };
      _self.order.suborders.forEach(function (suborder) {
        suborder.items.forEach(function (item) {
          Vue.set(item, "original_fulfilled_quantity", item.fulfilled_quantity);
        });
      });

      _self.$loader.$emit("trigger", false);
    })
    .catch((error) => {
      _self.$loader.$emit("trigger", false);
      if (error.response) {
        _self.$swal("Attention", error.response.data.error_message);
      } else {
        _self.$logger.notify(new Error(error));
      }
    });
}

function updatePackingSlip(e, packingSlip, scope) {
  const _self = this;

  _self.$validator.validateAll(scope).then((valid) => {
    if (!valid) {
      return;
    }
    this.updatePackingSlipButtonDisabled = true;
    _self.$loader.$emit("trigger", true);

    OrderApiService.updatePackingSlip(packingSlip)
      .then(() => {
        _self.$swal("Success", "Shipping carrier, tracking number and shipping cost information successfully saved!");
        _self.getData();
        this.$ma.trackEvent({
          action: "Shipping Info Saved",
          properties: {
            Scope: scope,
            "Tracking Number": packingSlip.tracking_number,
            "Shipping Cost": packingSlip.shipping_cost,
            Carrier: packingSlip.shipping_carrier,
          },
        });
      })
      .catch((error) => {
        if (error.response) {
          _self.$swal("Attention", error.response.data.error_message);
        } else {
          _self.$logger.notify(new Error(error));
        }
      })
      .finally(() => {
        _self.$loader.$emit("trigger", false);
        this.updatePackingSlipButtonDisabled = false;
      });
  });
}

function getShippingCarriers() {
  const _self = this;

  OrderApiService.getShippingCarriers().then(({ data }) => {
    _self.carriers = [];

    data.forEach((carrier) => {
      _self.carriers.push({ name: carrier.shipping_carrier });
    });
  });
}

function getData() {
  this.getShippingCarriers();

  if (this.user.role === "ADMIN") {
    this.getOrder();
  } else {
    this.getSuborder();
  }

  this.setUpShopify(true);
}

function isAdmin() {
  return this.user.role === "ADMIN";
}

function canSubsequentlyApproveRequest(suborder) {
  return suborder.status === "DECLINED" || suborder.status === "EXPIRED";
}

function canSubsequentlyDeclineRequest(suborder) {
  return (
    suborder.status === "APPROVED" || suborder.status === "FULFILLMENT_IN_PROGRESS" || suborder.status === "SHIPPED"
  );
}

function fulfilledQuantityIsEditable(status) {
  // FF quantity can be edited as long as order is not being shipped
  if (
    this.isShopifyConnected &&
    (status === "APPROVED" ||
      status === "PARTIALLY_FULFILLED" ||
      status === "FULFILLMENT_IN_PROGRESS" ||
      status === "SHIPPED" ||
      status === "PAID")
  ) {
    return false;
  }
  return (
    status !== "PARTIALLY_FULFILLED" &&
    status !== "FULFILLMENT_IN_PROGRESS" &&
    status !== "SHIPPED" &&
    status !== "PAID"
  );
}

function productFound(product) {
  this.$eventBus.$emit("show-quick-shop-modal", product, {
    isOrderAdjustmentModal: true,
    suborder: this.addToSuborder,
  });
}

function adjustmentModalCallback(selectedVariants) {
  this.$swal({
    title: "Are you sure?",
    text: "You will be adding a new item to this active order.",
    showCancelButton: true,
    cancelButtonText: "Cancel",
    confirmButtonText: "Yes",
  }).then((confirm) => {
    if (confirm.value) {
      this.addProductToOrder(selectedVariants);
    }
  });
}

async function addProductToOrder(selectedVariants) {
  const items = selectedVariants.map(({ variant, quantity }) => {
    return {
      variant_id: variant.id,
      ordered_quantity: quantity,
      fulfilled_quantity: quantity,
      shipped_quantity: quantity,
    };
  });

  try {
    await OrderApiService.addItemToOrder(this.addToSuborder.id, items);
    this.$eventBus.$emit("close-quick-shop-modal");
    this.getOrder();
  } catch (error) {
    if (error.response) {
      this.$swal("Attention", error.response.data.error_message);
    } else {
      this.$logger.notify(new Error(error));
    }
  }
}
